import { ApolloQueryResult, gql } from "@apollo/client/core";
import { graphQlManager } from "./services/server";

/**
 * Graphql query for executing a process flow.
 */
const executeProcessFlow = gql`
    mutation processFlowCreate($processFlowId: String!, $inputs: [String]!) {
        processExecutionCreate(processFlowId: $processFlowId, inputs: $inputs) {
            id
        }
    }
`;

/**
 * The response from the server when executing a process flow.
 */
interface ExecutionResponseGraphQL {
    processExecutions: ExecutionResponse;
}

/**
 * A list of node types available in executions
 */
export enum NodeType {
    Split = "Split",
    TextJoin = "TextJoin",
    TextSlice = "TextSlice",
    TextUpper = "TextUpper",
    TextLower = "TextLower",
    ParseJson = "ParseJson",
    TextBuilder = "TextBuilder",
    Design = "Design",
    OverlayImage = "OverlayImage",
    OverlayText = "OverlayText",
    TableGenerator = "TableGenerator",
    QR = "QR",
    CSV = "CSV",
    PDF = "PDF",
    PNG = "PNG",
    Email = "Email",
    LocationDelivery = "LocationDelivery",
    TemplateEmail = "TemplateEmail",
    Map = "Map",
    Sort = "Sort",
    Group = "Group",
    InnerJoin = "InnerJoin",
    Contains = "Contains",
    Flatten = "Flatten",
    KeyValuePairs = "KeyValuePairs",
    ListConcatenate = "ListConcatenate",
    Repeat = "Repeat",
    Size = "Size",
    ListBuilder = "ListBuilder",
    MapBuilder = "MapBuilder",
    PairBuilder = "PairBuilder",
    AND = "AND",
    OR = "OR",
    NOT = "NOT",
    Equals = "Equals",
    Present = "Present",
    ManualOperation = "ManualOperation",
    Switch = "Switch",
    AssetMetadata = "AssetMetadata",
    DataSelect = "DataSelect",
    LayoutSelect = "LayoutSelect",
    TransactionMetadata = "TransactionMetadata",
    VariantSelection = "VariantSelection",
    StartTerminal = "StartTerminal",
    EndTerminal = "EndTerminal",
    MiscNote = "MiscNote",
    Assign = "Assign",
    CsvVlookup = "CsvVlookup",
    ProcessFlow = "ProcessFlow",
    TemporaryAssetUrl = "TemporaryAssetUrl",
    WebRequest = "WebRequest",
}

interface ExecutionNodeResponse {
    id: string;
    type: NodeType;
    artifacts: string;
}

interface ExecutionResponse {
    id: string;
    nodes: ExecutionNodeResponse[];
    completedAt?: string;
    failedAt?: string;
}

/**
 * A service containing functionality for interacting with the Spiff Commerce API to execute and inspect the result of process flows.
 */
export class FlowService {
    /**
     *
     * @param id
     * @param inputs
     * @param options
     * @returns
     */
    async execute(
        id: string,
        inputs: FlowExecutionInput[],
        options?: { sleepTime?: number; repeats?: number },
    ): Promise<FlowExecutionResult> {
        // Fire the execution
        const createResponse = await graphQlManager
            .getShadowGraphqlClient()
            .mutate<{ processExecutionCreate: { id: string } }>({
                mutation: executeProcessFlow,
                variables: {
                    processFlowId: id,
                    inputs: [...inputs.map((input) => input.getValueForTransmission())],
                },
            });

        const executionId = createResponse.data?.processExecutionCreate?.id;
        if (!executionId) throw new Error("Failed to create process execution.");

        function delay(ms: number) {
            return new Promise((resolve) => {
                setTimeout(resolve, ms); // Convert seconds to milliseconds
            });
        }

        // Evaluate completion
        let completed: boolean = false;
        let repeats = 0;
        let response: ApolloQueryResult<ExecutionResponseGraphQL> | undefined;
        let flowResult: FlowExecutionResult | undefined = undefined;
        while (!completed) {
            response = await graphQlManager.getShadowGraphqlClient().query<ExecutionResponseGraphQL>({
                query: gql`
                    query getProcessFlowExecution($ids: [String]!) {
                        processExecutions(ids: $ids) {
                            id
                            completedAt
                            failedAt
                            nodes {
                                id
                                type
                                artifacts
                            }
                        }
                    }
                `,
                variables: {
                    ids: [executionId],
                },
            });
            const responseData = response.data?.processExecutions[0];

            // Check valid response
            if (!response || !responseData) throw new Error("Failed to retrieve server response for execution.");

            // If the execution failed prompt developer to access execution details in hub
            if (responseData.failedAt) {
                throw new Error(
                    "Execution has failed to complete. See Automation > FLows in Flight on partner account on SpiffCommerce Hub.",
                );
            }

            // Check for completion otherwise.
            if (responseData.completedAt) {
                completed = true;
                flowResult = new FlowExecutionResult(responseData);
                break;
            }

            repeats += 1;

            const maxRepeats = options?.repeats ? options?.repeats : 5;
            if (repeats >= maxRepeats) throw new Error("Maximum wait time exceeded for execution result.");
            await delay(options?.sleepTime ? Math.max(options.sleepTime, 500) : 2000);
        }
        if (!flowResult) throw new Error("Failed to retrieve server response for execution.");
        return Promise.resolve(flowResult);
    }
}

/**
 * Handles preparing a flow input for transmission to the server.
 */
export class FlowExecutionResult {
    protected readonly execution: ExecutionResponse;

    constructor(execution: ExecutionResponse) {
        this.execution = execution;
    }

    /**
     * @returns The raw response from the server.
     */
    getRaw(): ExecutionResponse {
        return this.execution;
    }

    /**
     * @returns The nodes contained within the execution.
     */
    getNodes(): FlowExecutionNodeResult[] {
        return this.execution.nodes.map((n) => new FlowExecutionNodeResult(n));
    }

    /**
     * @param type The type of node to return.
     * @returns A list of nodes matching the requested type.
     */
    getNodesByType(type: NodeType): FlowExecutionNodeResult[] {
        return this.execution.nodes.filter((n) => n.type === type).map((n) => new FlowExecutionNodeResult(n));
    }

    /**
     * @returns A list of input nodes that exist in this execution.
     */
    getInputs(): FlowExecutionNodeResult[] {
        return this.execution.nodes
            .filter((n) => n.type === NodeType.StartTerminal)
            .map((n) => new FlowExecutionNodeResult(n));
    }

    /**
     * @returns A list of out put nodes that exist in this execution.
     */
    getOutputs(): FlowExecutionNodeResult[] {
        return this.execution.nodes
            .filter((n) => n.type === NodeType.EndTerminal)
            .map((n) => new FlowExecutionNodeResult(n));
    }

    /**
     * @returns A date object representing the point in time this execution completed.
     */
    getCompletedAt(): Date | undefined {
        if (!this.execution.completedAt) return undefined;
        return new Date(this.execution.completedAt);
    }

    /**
     * @returns A date object representing the point in time this execution failed.
     */
    getFailedAt(): Date | undefined {
        if (!this.execution.failedAt) return undefined;
        return new Date(this.execution.failedAt);
    }
}

/**
 * Handles preparing a flow input for transmission to the server.
 */
export class FlowExecutionNodeResult {
    protected readonly node: ExecutionNodeResponse;

    constructor(node: ExecutionNodeResponse) {
        this.node = node;
    }

    getId(): string {
        return this.node.id;
    }

    getType(): NodeType {
        return this.node.type;
    }

    getArtifacts(): Map<string, any> {
        return new Map(Object.entries(JSON.parse(this.node.artifacts).value));
    }

    getArtifactByName<T>(name: string): T {
        return this.getArtifacts().get(name) as T;
    }
}

/**
 * Handles preparing a flow input for transmission to the server.
 */
abstract class FlowExecutionInput {
    protected readonly value: string;
    constructor(value: string) {
        this.value = value;
    }
    getRaw(): string {
        return this.value;
    }
    getValueForTransmission(): string {
        const v = this.value;
        if (v.startsWith("[")) {
            return JSON.stringify(JSON.parse(v));
        } else {
            return v;
        }
    }
}

/**
 * Represents the type of object being referenced by the input.
 */
export const enum ObjectInputType {
    Transaction = "Transaction",
    Bundle = "Bundle",
    Product = "Product",
    Variant = "Variant",
    Option = "Option",
    LineItem = "LineItem",
    Asset = "Asset",
}

export class TextInput extends FlowExecutionInput {
    constructor(input: string) {
        super(`"${input}"`);
    }
}

/**
 * Handles validation of spiffObject structure for transmission to the server.
 */
export class ObjectInput extends FlowExecutionInput {
    constructor(id: string, type: ObjectInputType) {
        // Assets aren't a UUID but most other assets will be UUID values.
        if (type !== ObjectInputType.Asset && !ObjectInput.validUUID(id))
            throw new Error("Invalid ID, must be a valid v4 UUID. https://www.uuidgenerator.net/");
        // The server expects a specific structure for representing a reference to an object in our platform.
        super(`{"isSpiffObject": true, "id": "${id}", "type": "${type}"}`);
    }
    static validUUID(uuid: string) {
        return uuid.match(/^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i);
    }
}

/**
 * Handles validation of array input for transmission to the server.
 */
export class ArrayInput extends FlowExecutionInput {
    constructor(elements: FlowExecutionInput[]) {
        super(`[${elements.map((e) => e.getRaw()).join(",")}]`);
    }
}
